/**
 * Copyright (c) 2018-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Interval } from '../mol-data/int';
import { BitFlags } from './bit-flags';
import { assertUnreachable } from './type-helpers';
export var MarkerAction;

(function (MarkerAction) {
  MarkerAction[MarkerAction["None"] = 0] = "None";
  MarkerAction[MarkerAction["Highlight"] = 1] = "Highlight";
  MarkerAction[MarkerAction["RemoveHighlight"] = 2] = "RemoveHighlight";
  MarkerAction[MarkerAction["Select"] = 4] = "Select";
  MarkerAction[MarkerAction["Deselect"] = 8] = "Deselect";
  MarkerAction[MarkerAction["Toggle"] = 16] = "Toggle";
  MarkerAction[MarkerAction["Clear"] = 32] = "Clear";
})(MarkerAction || (MarkerAction = {}));

export var MarkerActions;

(function (MarkerActions) {
  MarkerActions.is = BitFlags.has;
  MarkerActions.All = MarkerAction.Highlight | MarkerAction.RemoveHighlight | MarkerAction.Select | MarkerAction.Deselect | MarkerAction.Toggle | MarkerAction.Clear;
  MarkerActions.Highlighting = MarkerAction.Highlight | MarkerAction.RemoveHighlight | MarkerAction.Clear;
  MarkerActions.Selecting = MarkerAction.Select | MarkerAction.Deselect | MarkerAction.Toggle | MarkerAction.Clear;
})(MarkerActions || (MarkerActions = {}));

export function applyMarkerActionAtPosition(array, i, action) {
  switch (action) {
    case MarkerAction.Highlight:
      array[i] |= 1;
      break;

    case MarkerAction.RemoveHighlight:
      array[i] &= ~1;
      break;

    case MarkerAction.Select:
      array[i] |= 2;
      break;

    case MarkerAction.Deselect:
      array[i] &= ~2;
      break;

    case MarkerAction.Toggle:
      array[i] ^= 2;
      break;

    case MarkerAction.Clear:
      array[i] = 0;
      break;
  }
}
export function applyMarkerAction(array, set, action) {
  if (action === MarkerAction.None) return false;

  if (Interval.is(set)) {
    var start = Interval.start(set);
    var end = Interval.end(set);
    var view = new Uint32Array(array.buffer, 0, array.buffer.byteLength >> 2);
    var viewStart = start + 3 >> 2;
    var viewEnd = viewStart + (end - 4 * viewStart >> 2);
    var frontStart = start;
    var frontEnd = Math.min(4 * viewStart, end);
    var backStart = Math.max(start, 4 * viewEnd);
    var backEnd = end;

    switch (action) {
      case MarkerAction.Highlight:
        for (var i = viewStart; i < viewEnd; ++i) view[i] |= 0x01010101;

        break;

      case MarkerAction.RemoveHighlight:
        for (var i = viewStart; i < viewEnd; ++i) view[i] &= ~0x01010101;

        break;

      case MarkerAction.Select:
        for (var i = viewStart; i < viewEnd; ++i) view[i] |= 0x02020202;

        break;

      case MarkerAction.Deselect:
        for (var i = viewStart; i < viewEnd; ++i) view[i] &= ~0x02020202;

        break;

      case MarkerAction.Toggle:
        for (var i = viewStart; i < viewEnd; ++i) view[i] ^= 0x02020202;

        break;

      case MarkerAction.Clear:
        for (var i = viewStart; i < viewEnd; ++i) view[i] = 0;

        break;

      default:
        assertUnreachable(action);
    }

    for (var i = frontStart; i < frontEnd; ++i) {
      applyMarkerActionAtPosition(array, i, action);
    }

    for (var i = backStart; i < backEnd; ++i) {
      applyMarkerActionAtPosition(array, i, action);
    }
  } else {
    switch (action) {
      case MarkerAction.Highlight:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] |= 1;

        break;

      case MarkerAction.RemoveHighlight:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] &= ~1;

        break;

      case MarkerAction.Select:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] |= 2;

        break;

      case MarkerAction.Deselect:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] &= ~2;

        break;

      case MarkerAction.Toggle:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] ^= 2;

        break;

      case MarkerAction.Clear:
        for (var i = 0, il = set.length; i < il; ++i) array[set[i]] = 0;

        break;

      default:
        assertUnreachable(action);
    }
  }

  return true;
}